import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWorkspace } from './WorkspaceContext';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import languagesData from '../data/languages.json';

interface LanguageContextType {
  availableLanguages: string[];
  selectedLanguage: string | null;
  firstLanguage: string | null;
  isLoading: boolean;
  error: string | null;
  setSelectedLanguage: (language: string) => void;
  getLanguageName: (langCode: string | null) => string;
  addLanguage: (language: string) => Promise<void>;
  deleteLanguage: (language: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType>({
  availableLanguages: [],
  selectedLanguage: null,
  firstLanguage: null,
  isLoading: false,
  error: null,
  setSelectedLanguage: () => {},
  getLanguageName: () => '',
  addLanguage: async () => {},
  deleteLanguage: async () => {}
});

export const useLanguages = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedProject, selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [firstLanguage, setFirstLanguage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load languages when project changes
  useEffect(() => {
    const loadLanguages = async () => {
      if (!selectedProject) {
        setAvailableLanguages([]);
        setFirstLanguage(null);
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const { data: project, error: projectError } = await supabase
          .from('Projects')
          .select('languages')
          .eq('id', selectedProject.id)
          .single();

        if (projectError) throw projectError;

        const languages = project?.languages || [];
        setAvailableLanguages(languages);
        
        // Set first language
        if (languages.length > 0) {
          setFirstLanguage(languages[0]);
          
          // Set selected language from localStorage or default to first
          const savedLanguage = user ? localStorage.getItem(`${user.id}_selectedLanguage_${selectedProject.id}`) : null;
          const languageToSelect = savedLanguage && languages.includes(savedLanguage) 
            ? savedLanguage 
            : languages[0];
          
          setSelectedLanguage(languageToSelect);
          
          // Store selection in localStorage
          if (user) {
            localStorage.setItem(`${user.id}_selectedLanguage_${selectedProject.id}`, languageToSelect);
          }
        }
      } catch (error: any) {
        console.error('Error loading languages:', error);
        setError(error.message || 'Failed to load languages');
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguages();
  }, [selectedProject, user]);

  const handleSetSelectedLanguage = (language: string) => {
    setSelectedLanguage(language);
    if (user && selectedProject) {
      localStorage.setItem(`${user.id}_selectedLanguage_${selectedProject.id}`, language);
    }
  };

  const getLanguageName = (langCode: string | null) => {
    if (!langCode) return 'No language';
    const language = languagesData.languages.find(
      lang => lang.code.toLowerCase() === langCode.toLowerCase() ||
             lang.code.toLowerCase().replace(/-/g, '_') === langCode.toLowerCase()
    );
    return language ? language.name : langCode;
  };

  const addLanguage = async (language: string) => {
    if (!selectedProject) return;
    
    try {
      const currentLanguages = [...availableLanguages];
      if (!currentLanguages.includes(language)) {
        const newLanguages = [...currentLanguages, language];
        const { error: updateError } = await supabase
          .from('Projects')
          .update({ languages: newLanguages })
          .eq('id', selectedProject.id);

        if (updateError) throw updateError;
        setAvailableLanguages(newLanguages);
      }
    } catch (error: any) {
      console.error('Error adding language:', error);
      setError(error.message || 'Failed to add language');
    }
  };

  const deleteLanguage = async (language: string) => {
    if (!selectedProject) return;
    
    try {
      const newLanguages = availableLanguages.filter(l => l !== language);
      const { error: updateError } = await supabase
        .from('Projects')
        .update({ languages: newLanguages })
        .eq('id', selectedProject.id);

      if (updateError) throw updateError;
      setAvailableLanguages(newLanguages);
      
      // If deleted language was selected, switch to first available
      if (selectedLanguage === language && newLanguages.length > 0) {
        handleSetSelectedLanguage(newLanguages[0]);
      }
    } catch (error: any) {
      console.error('Error deleting language:', error);
      setError(error.message || 'Failed to delete language');
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        availableLanguages,
        selectedLanguage,
        firstLanguage,
        isLoading,
        error,
        setSelectedLanguage: handleSetSelectedLanguage,
        getLanguageName,
        addLanguage,
        deleteLanguage
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}; 